import { ListSubheader, List } from "@mui/material";
import { useLocation, matchPath } from "react-router-dom";
import SidebarMenuItem from "./item";
import { RenderSidebarMenuItemList } from "./items";
import { experimentalStyled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import useAuth from "src/hooks/useAuth";

const MenuWrapper = experimentalStyled(List)(
  ({ theme }) => `
    margin-bottom: ${theme.spacing(1)};
    padding: 0;

    .MuiListSubheader-root {
      text-transform: none;
      font-size: ${theme.typography.pxToRem(11)};
      font-family: ${theme.sidebar.fontFamily};
      font-weight: 600;
      color: ${theme.sidebar.menuItemHeadingColor};
      padding: ${theme.spacing(1, 2)};
      line-height: 1.5;
      letter-spacing: 0.05em;
    }
`
);

const SubMenuWrapper = experimentalStyled(List)(
  ({ theme }) => `
    &.MuiList-root {
      padding: 0;
      .MuiListItem-root {
        padding: 0 0 12px 0;

        .MuiButton-label {
          display: flex;
          position: relative;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(2)};

            .MuiBadge-standard {
              background: ${theme.colors.primary.main};
              font-size: ${theme.typography.pxToRem(11)};
              color: ${theme.palette.primary.contrastText};
            }
          }
        }

        .MuiButton-root {
          display: flex;
          color: ${theme.sidebar.menuItemTextColor};
          background-color: ${theme.sidebar.menuItemBg};
          width: 100%;
          border-radius: ${theme.sidebar.menuItemBorderRadius};
          justify-content: flex-start;
          font-size: ${theme.sidebar.menuItemFontSize};
          font-family: ${theme.sidebar.fontFamily};
          padding: 14px 16px;
          font-weight: 400;

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(["color"])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            font-size: ${theme.typography.pxToRem(18)};
            margin-right: ${theme.spacing(1.5)};
            color: ${theme.sidebar.menuItemIconColor};
          }

          .MuiButton-endIcon {
            margin-left: auto;
            font-size: ${theme.typography.pxToRem(16)};
          }

          &:hover {
            background-color: transparent;
            color: ${theme.palette.primary.main};
            .MuiButton-startIcon,
            .MuiButton-endIcon {
                color: ${theme.palette.primary.main};
            }
          }

          &.Mui-active {
            background-color: ${theme.sidebar.menuItemBgActive};
            color: ${theme.sidebar.menuItemColorActive};
            font-weight: 600;
            .MuiButton-startIcon,
            .MuiButton-endIcon {
                color: ${theme.sidebar.menuItemIconColorActive};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;
          line-height: 1;
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(0.5, 0, 0.5, 2)};
          }

          .MuiListItem-root {
            padding: 0 0 6px 0;

            .MuiButton-root {
              font-size: ${theme.typography.pxToRem(13)};
              font-family: ${theme.sidebar.fontFamily};
              padding: 8px 12px 8px 24px;
              font-weight: 400;
              color: ${theme.sidebar.menuItemTextColor};

              &:before {
                content: '';
                background: ${theme.sidebar.menuItemIconColor};
                opacity: .4;
                transition: ${theme.transitions.create(["background"])};
                width: 2px;
                height: 24px;
                margin-right: ${theme.spacing(2)};
                flex-shrink: 0;
              }

              &.Mui-active,
              &:hover {
                background-color: ${theme.sidebar.menuItemBgActive};
                color: ${theme.sidebar.menuItemColorActive};

                &:before {
                  background: ${theme.sidebar.menuItemIconColorActive};
                  opacity: 1;
                  width: 3px;
                  border-radius: 20px;
                }
              }
            }
          }
        }
      }
    }
`
);

function renderSidebarMenuItems({ items, path, partner_level }) {
  return (
    <SubMenuWrapper>
      {items.reduce((ev, item) => {
        if (
          item.name === "Organization Transactions" ||
          item.name === "Organization Invoices"
        ) {
          if (partner_level !== "L1")
            return reduceChildRoutes({ ev, item, path, partner_level });
          else return ev;
        }
        return reduceChildRoutes({ ev, item, path, partner_level });
      }, [])}
    </SubMenuWrapper>
  );
}

function reduceChildRoutes({ ev, path, item, partner_level }) {
  const key = item.name;

  if (item.items) {
    const open = matchPath(path, {
      path: item.link,
      exact: false,
    });

    ev.push(
      <SidebarMenuItem
        key={key}
        open={Boolean(open)}
        name={item.name}
        icon={item.icon}
        badge={item.badge}
      >
        {renderSidebarMenuItems({
          path,
          items: item.items,
          partner_level,
        })}
      </SidebarMenuItem>
    );
  } else {
    ev.push(
      <SidebarMenuItem
        key={key}
        name={item.name}
        link={item.link}
        badge={item.badge}
        icon={item.icon}
      />
    );
  }

  return ev;
}

function SidebarMenu() {
  const location = useLocation();
  const { t } = useTranslation();
  const { user, is_local_rails_enabled } = useAuth();
  const { partner_data } = useAuth();
  const { partner_level = "L1" } = partner_data || {};
  const menuItems = RenderSidebarMenuItemList(is_local_rails_enabled);
  return (
    <>
      {menuItems.map((section) => (
        <MenuWrapper
          key={section.heading}
          subheader={
            <ListSubheader disableSticky>{t(section.heading)}</ListSubheader>
          }
        >
          {renderSidebarMenuItems({
            items: user.is_admin
              ? section.items
              : section.items.filter((item) => {
                  if (item.name !== "Admin Accounts") {
                    return item;
                  }
                }),
            path: location.pathname,
            partner_level,
          })}
        </MenuWrapper>
      ))}
    </>
  );
}

export default SidebarMenu;
